import { useState, useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const categories = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies / Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Pants / Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { name: 'Underwear / Underpants', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

const articleLinks = [
  { label: 'Complete Guide', path: '/basetao-spreadsheet-guide' },
  { label: 'Review 2026', path: '/basetao-spreadsheet-review' },
  { label: 'Shipping Cost', path: '/basetao-shipping-cost' },
  { label: 'Warehouse Guide', path: '/basetao-warehouse-guide' },
  { label: 'Coupon & Discount', path: '/basetao-coupon-code' },
  { label: 'Is Basetao Legit?', path: '/is-basetao-legit' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(!isHome);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopGuideOpen, setShopGuideOpen] = useState(false);
  const [catMobileOpen, setCatMobileOpen] = useState(false);
  const shopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shopRef.current && !shopRef.current.contains(e.target as Node)) {
        setShopGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (e: ReactMouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      setMobileOpen(false);
      setCatMobileOpen(false);
      if (!isHome) {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return;
      }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeAll = () => {
    setMobileOpen(false);
    setCatMobileOpen(false);
    setShopGuideOpen(false);
  };

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Categories', href: '#categories' },
    { label: 'About', href: '#about' },
    { label: 'Review', href: '#review' },
    { label: 'FAQ', href: '#faq' },
  ];

  const isTransparent = isHome && !scrolled;

  const navItemClass = isTransparent
    ? 'text-white/90 hover:text-white hover:bg-white/10'
    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-gradient-to-b from-black/45 via-black/25 to-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60 shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            onClick={closeAll}
            className={`font-heading text-xl md:text-2xl transition-colors duration-300 ${
              isTransparent ? 'text-white' : 'text-foreground-950'
            }`}
          >
            Basetao Sheet
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.href === '/' ? (
                <Link
                  key={link.href}
                  to="/"
                  onClick={closeAll}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${navItemClass}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${navItemClass}`}
                >
                  {link.label}
                </a>
              ),
            )}

            <div ref={shopRef} className="relative">
              <button
                onClick={() => setShopGuideOpen(!shopGuideOpen)}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${navItemClass}`}
              >
                Shopping Guide
                {shopGuideOpen ? <i className="ri-arrow-up-s-line text-xs"></i> : <i className="ri-arrow-down-s-line text-xs"></i>}
              </button>
              {shopGuideOpen && (
                <div className="absolute top-full right-0 mt-1 w-56 bg-background-50 rounded-lg border border-background-200/60 shadow-lg py-2 z-50">
                  {articleLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeAll}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-background-200/60 mt-1 pt-1">
                    {categories.slice(0, 6).map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeAll}
                        className="block px-4 py-2 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`ml-2 px-5 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ${
                isTransparent
                  ? 'bg-white text-foreground-950 hover:bg-white/90'
                  : 'btn-primary'
              }`}
            >
              Buy It Now
            </a>
          </div>

          <button
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
              isTransparent ? 'text-white' : 'text-foreground-950'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <i className="ri-close-line text-2xl"></i> : <i className="ri-menu-line text-2xl"></i>}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/60 shadow-lg">
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-0.5 mb-4">
              <p className="text-xs text-foreground-400 uppercase tracking-wide px-3 py-2 font-medium">Navigation</p>
              {navLinks.map((link) =>
                link.href === '/' ? (
                  <Link
                    key={link.href}
                    to="/"
                    onClick={closeAll}
                    className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-md transition-colors flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-md transition-colors flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    {link.label}
                  </a>
                ),
              )}
            </div>

            <div className="mb-4">
              <p className="text-xs text-foreground-400 uppercase tracking-wide px-3 py-2 font-medium">Shopping Guide</p>
              {articleLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeAll}
                  className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-md transition-colors flex items-center gap-2"
                >
                  <i className="ri-article-line text-foreground-400"></i>
                  {link.label}
                </Link>
              ))}
            </div>

            <button
              onClick={() => setCatMobileOpen(!catMobileOpen)}
              className="w-full px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-foreground-400"></i>
                Browse Categories
              </span>
              {catMobileOpen ? <i className="ri-arrow-up-s-line text-foreground-400"></i> : <i className="ri-arrow-down-s-line text-foreground-400"></i>}
            </button>
            {catMobileOpen && (
              <div className="mt-1 ml-4 grid grid-cols-2 gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="px-3 py-2 text-xs text-foreground-600 hover:bg-accent-50 hover:text-accent-700 rounded-md transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="mt-4 block text-center btn-primary text-sm"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}